import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export default class Testimonials {
  constructor() {
    this.classNames = {
      block: 'b-af-testimonials',
      blockBody: 'b-af-testimonials__body',
      button: 'b-af-testimonials__button',
      background: 'c-background',
      isOverflowHidden: 'is-overflow-hidden',
    }

    this.blocks = this.getTestimonialsBlocks();

    if (this.blocks.length > 0) {
      this.init(this.blocks);
    }
  }

  init(blocks) {
    gsap.registerPlugin(ScrollTrigger);

    blocks.forEach(block => {
      const background = block.querySelector(`.${this.classNames.background}`);
      const button = block.querySelector(`.${this.classNames.button}`);
      const body = block.querySelector(`.${this.classNames.blockBody}`);

      if (body) {
        gsap.set(body, {
          marginTop: -20,
          opacity: 0,
        });

        gsap.to(body, {
          marginTop: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: body,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        });
      }

      if (!button || !background) {
        return;
      }

      const modal = this.getModalByButton(button);

      if (!modal) {
        return;
      }

      const modalIframe = modal.querySelector('iframe');

      this.handleIframeSrc({ iframe: modalIframe });
      this.handleModalEvents({ modal, block });
      this.clearButtonFromDefaults(button);

      button.addEventListener('click', (event) => {
        event.preventDefault();

        const backgroundScale = this.getScaleValue({
          background,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        });

        gsap.to(background, {
          duration: 0.5,
          ease: 'linear',
          scale: backgroundScale,
          onStart: () => {
            document.documentElement.classList.add('is-overflow-hidden', 'is-af-testimonials-active');
          },
          onUpdate: () => {
            const { y } = block.getBoundingClientRect();
            gsap.to(background, {
              y: y * -1,
            });
          },
        });

        gsap.to(modal, {
          duration: 0.5,
          autoAlpha: 1,
          ease: 'linear',
          delay: 0.9,
          onComplete: () => {
            this.onModalOpen({ modal });
          },
        })

        this.handleButtonClick({ block })

        this.togglePageOverflow();
      });

      if (window.innerWidth < 768) {
        let isScrolling;

        window.addEventListener('scroll', function() {
          window.clearTimeout(isScrolling);

          button.forEach(function(anchor) {
            anchor.style.pointerEvents = 'none';
          });

          isScrolling = setTimeout(function() {
            button.forEach(function(anchor) {
              anchor.style.pointerEvents = 'auto';
            });
          }, 300);
        }, false);
      }

    });
  }

  handleModalEvents({ modal, block }) {
    if (!modal) {
      return;
    }
    modal.addEventListener('click', (event) => {
      if (event.target === modal || (event.target.dataset.dismiss && event.target.dataset.dismiss === 'modal')) {
        this.closeModal({ modal, block });
      }
    });
  }

  closeModal({ modal, block }) {
    const background = block.querySelector(`.${this.classNames.background}`);

    gsap.to(modal, {
      duration: 0.5,
      autoAlpha: 0,
      ease: 'linear',
      onStart: () => {
        this.onModalClose({ modal });
      },
    });

    gsap.to(background, {
      duration: 0.5,
      scale: 1,
      y: 0,
      x: 0,
      ease: 'linear',
      onStart: () => {
        document.documentElement.classList.remove('is-overflow-hidden', 'is-af-testimonials-active');
      },
      delay: 0.5,
    });

    this.handleButtonClick({ block });

    this.togglePageOverflow();
  }

  handleButtonClick({ block }) {
    this.toggleBlock(block);
  }

  getScaleValue({ background, viewportWidth, viewportHeight }) {
    const backgroundSize = background.getBoundingClientRect();

    const backgroundWidth = backgroundSize.width;
    const backgroundHeight = backgroundSize.height;

    const scaleX = viewportWidth / backgroundWidth;
    const scaleY = viewportHeight / backgroundHeight;
    const scale = Math.max(scaleX, scaleY);

    return scale;
  }

  handleIframeSrc({ iframe }) {
    if (!iframe) {
      return;
    }

    const originalSrc = iframe.getAttribute('src');
    const autoplay = 'autoplay=1';
    iframe.dataset.originalSrc = originalSrc;

    if (originalSrc.includes(autoplay)) {
      return;
    }

    if (originalSrc.indexOf('?') > 0) {
      iframe.dataset.playSrc = `${originalSrc}&${autoplay}`;
      return;
    }

    iframe.dataset.playSrc = `${originalSrc}?${autoplay}`;
  }

  onModalOpen({ modal }) {
    const iframe = modal.querySelector('iframe');

    if (iframe) {
      iframe.setAttribute('src', iframe.dataset.playSrc);
    }
  }

  onModalClose({ modal }) {
    const iframe = modal.querySelector('iframe');

    if (iframe) {
      iframe.setAttribute('src', iframe.dataset.originalSrc);
    }
  }

  togglePageOverflow() {
    document.body.classList.toggle(this.classNames.isOverflowHidden);
    document.documentElement.classList.toggle(this.classNames.isOverflowHidden);
  }

  toggleBlock(block) {
    block.classList.toggle('is-active');
  }

  getModalByButton(button) {
    const target = button.getAttribute('data-target');
    return document.querySelector(target);
  }

  clearButtonFromDefaults(button) {
    if (!button) {
      return;
    }

    button.removeAttribute('data-toggle');
    this.cleanButtonModalAttributes(button);
  }

  cleanButtonModalAttributes(button) {
    const modal = this.getModalByButton(button);

    if (!modal) {
      return;
    }

    modal.removeAttribute('class');
    modal.classList.add('modal', 'af-modal-video');
  }

  getTestimonialsBlocks() {
    return Array.from(document.querySelectorAll(`.${this.classNames.block}`));
  }
}



// WEBPACK FOOTER //
// ./scripts/blocks/testimonials.js